import { editorClient } from "@/lib/editorClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import z from "zod";
import { Trans } from "@lingui/react/macro";
import { redirect, notFound } from "@tanstack/react-router";
import { languageKeys, languageMap } from "@op/puck-types";
import { OverviewPageForm } from "@/components/app/topic-settings/OverviewPageForm";

async function getOverviewPageSettingsData({
  organizationSlug,
  projectSlug,
  languageKey,
  topicSlug,
}: ParamsSchema) {
  const res = await editorClient.topics[":organizationSlug"][":projectSlug"][
    ":languageKey"
  ][":topicSlug"].settings["overview-page"].$get({
    param: {
      organizationSlug,
      projectSlug,
      languageKey,
      topicSlug,
    },
  });
  if (!res.ok) {
    if (res.status === 401) throw redirect({ to: "/login" });
    if (res.status === 404) throw notFound();
    throw new Error("Failed to get overview page settings");
  }
  const overviewPageSettings = await res.json();
  return overviewPageSettings;
}

const overviewPageSettingsQueryKey = (params: ParamsSchema) => [
  "editor-topic-settings-overview-page",
  params,
];

const paramsSchema = z.object({
  organizationSlug: z.string(),
  projectSlug: z.string(),
  languageKey: z.enum(languageKeys),
  topicSlug: z.string(),
});

type ParamsSchema = z.infer<typeof paramsSchema>;

export const Route = createFileRoute(
  "/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/settings"
)({
  component: RouteComponent,
  params: paramsSchema,
  loader: async ({ params, context: { queryClient } }) => {
    const overviewPageSettings = await getOverviewPageSettingsData(params);
    queryClient.setQueryData(
      overviewPageSettingsQueryKey(params),
      overviewPageSettings
    );
  },
});

function RouteComponent() {
  const params = Route.useParams();
  const queryClient = useQueryClient();
  const { organizationSlug, projectSlug, languageKey, topicSlug } = params;

  const { data: overviewPageSettings } = useQuery(
    {
      queryKey: overviewPageSettingsQueryKey(params),
      queryFn: () => getOverviewPageSettingsData(params),
    },
    queryClient
  );

  return (
    <div className="container max-w-none flex w-full flex-col items-center gap-6 bg-default-background py-12 overflow-auto">
      <div className="flex w-full max-w-[576px] flex-col items-start gap-6">
        <div className="flex w-full flex-col items-start gap-1">
          <span className="w-full text-heading-2 font-heading-2 text-default-font">
            <Trans>Topic settings</Trans>
          </span>
          <span className="text-body font-body text-subtext-color">
            <Trans>
              Topic settings for {projectSlug} - {languageMap[languageKey]} -{" "}
              {topicSlug}
            </Trans>
          </span>
        </div>
        <OverviewPageForm
          overviewPageSettings={overviewPageSettings ?? null}
          organizationSlug={organizationSlug}
          projectSlug={projectSlug}
          languageKey={languageKey}
          topicSlug={topicSlug}
        />
      </div>
    </div>
  );
}
